/**
 * Property builders shared across collections.
 *
 * Two shapes recur throughout the content model and both are easy to get subtly
 * wrong once per field: dates, which are ISO strings rather than Timestamps, and
 * relationships, which are plain string ids rather than Firestore references.
 * Defining them once is the difference between a model the exporter can read and
 * one it cannot.
 */

import { ArrayProperty, buildProperty, MapProperty, StringProperty } from "@firecms/core";
import { IsoDateField, IsoDateFieldProps } from "../fields/IsoDateField";
import { ImageRefField } from "../fields/ImageRefField";

interface IsoDateOptions {
    description?: string;
    required?: boolean;
    mode?: IsoDateFieldProps["mode"];
    hideFromCollection?: boolean;
}

export const isoDate = (name: string, options: IsoDateOptions = {}): StringProperty =>
    buildProperty({
        dataType: "string" as const,
        name,
        ...(options.description ? { description: options.description } : {}),
        ...(options.hideFromCollection ? { hideFromCollection: true } : {}),
        validation: { required: options.required ?? false },
        Field: IsoDateField,
        customProps: { mode: options.mode ?? "date_time" }
    });

interface RefOptions {
    description?: string;
    required?: boolean;
    previewProperties?: string[];
    hideFromCollection?: boolean;
}

/**
 * A relationship stored as the target's document id.
 *
 * `dataType: "string"` with a `reference` config, not `dataType: "reference"`.
 * A FireCMS reference property writes a Firestore `DocumentReference`, but every
 * relationship in `types.ts` is typed `string` and the exporter resolves it by
 * looking the id up in a map. Writing a DocumentReference would break that
 * lookup and put an unserializable object in the snapshot.
 */
export const idRef = (name: string, path: string, options: RefOptions = {}): StringProperty =>
    buildProperty({
        dataType: "string" as const,
        name,
        ...(options.description ? { description: options.description } : {}),
        ...(options.hideFromCollection ? { hideFromCollection: true } : {}),
        validation: { required: options.required ?? false },
        reference: {
            dataType: "reference" as const,
            path,
            ...(options.previewProperties ? { previewProperties: options.previewProperties } : {})
        }
    });

export const idRefArray = (name: string, path: string, options: RefOptions = {}): ArrayProperty =>
    buildProperty({
        dataType: "array" as const,
        name,
        ...(options.description ? { description: options.description } : {}),
        ...(options.hideFromCollection ? { hideFromCollection: true } : {}),
        validation: { required: options.required ?? false },
        of: {
            dataType: "string" as const,
            reference: {
                dataType: "reference" as const,
                path,
                ...(options.previewProperties
                    ? { previewProperties: options.previewProperties }
                    : {})
            }
        }
    });

export const stringArray = (name: string, description?: string): ArrayProperty =>
    buildProperty({
        dataType: "array" as const,
        name,
        ...(description ? { description } : {}),
        of: { dataType: "string" as const }
    });

/**
 * A stored image.
 *
 * The sub-properties are still declared, because they are what the validation
 * schema is built from and `alt` must stay required, but none of them is
 * rendered by FireCMS: `ImageRefField` draws the whole map. That is what lets
 * `width` and `height` be read off the file instead of typed, and lets an
 * oversized upload be downscaled before it is stored.
 */
export const mediaRef = (name: string, description: string, folder = "guides"): MapProperty =>
    buildProperty({
        dataType: "map" as const,
        name,
        description,
        expanded: false,
        /* Absent, not an empty shell. FireCMS seeds a map with a default built
           from its children, so without this a new guide starts life carrying
           `{ alt: null }` and fails `alt`'s required rule before anyone has
           uploaded anything — a form that cannot be saved and does not say why.
           `null` is the one value `getDefaultValueFor` passes through. */
        defaultValue: null,
        Field: ImageRefField,
        customProps: { storagePath: folder },
        properties: {
            id: {
                dataType: "string" as const,
                name: "Media id"
            },
            storagePath: {
                dataType: "string" as const,
                name: "Image",
                description: "Stored as a Cloud Storage path. URLs are built at render time."
            },
            alt: {
                dataType: "string" as const,
                name: "Alt text",
                description: "Required. A decorative-only image should be removed instead.",
                validation: { required: true }
            },
            /* Read from the decoded file by `ImageRefField`, never asked for. */
            width: { dataType: "number" as const, name: "Width" },
            height: { dataType: "number" as const, name: "Height" },

            /* Declared so the model is visible here and not only in the
               uploader. Written by `ImageRefField` from what it actually
               encoded and stored, and by nothing else: the renderer treats
               this list as the authority on which objects exist, so a hand-
               typed entry would be a broken image rather than a wrong one. */
            renditions: {
                dataType: "array" as const,
                name: "Renditions",
                description:
                    "Extra encodings produced at upload. Recorded only when the upload " +
                    "succeeded, because the renderer trusts this list absolutely.",
                of: {
                    dataType: "map" as const,
                    properties: {
                        format: {
                            dataType: "string" as const,
                            name: "Format",
                            enumValues: { avif: "AVIF", webp: "WebP", jpeg: "JPEG", png: "PNG" }
                        },
                        width: { dataType: "number" as const, name: "Width" },
                        height: { dataType: "number" as const, name: "Height" },
                        storagePath: { dataType: "string" as const, name: "Object path" },
                        bytes: { dataType: "number" as const, name: "Bytes" }
                    }
                }
            },

            /* Written by copy-on-publish in `functions/src/publicMedia.ts`.
               Declared here so a CMS save cannot be the thing that loses it. */
            publicPath: {
                dataType: "string" as const,
                name: "Public path",
                readOnly: true,
                description:
                    "Set by the publish pipeline once the image has been copied to the " +
                    "public media bucket. Until it exists the site renders no image at all."
            }
        }
    });
